/**
 * The one line a client page shows somebody whose role may not read it.
 *
 * `.docs/19-client-portal-pages.md` §Edge cases: "`billing` role user: sees billing,
 * settings (payment), invoices; requests/projects/files show the `Forbidden` problem
 * **inline as the one line**, nav items hidden." Three pages say it, so it is written
 * once — the same rule §DRY watchlist applies to `DecisionCard` and `PeriodSeg`.
 *
 * **It is an `Empty`, and that is the whole of the chrome.** DESIGN.md P7: "empty
 * states are one line, not a bordered panel with an icon and a three-sentence
 * paragraph." A refusal is the same shape as an absence — there is nothing here —
 * and a `Notice need` would have made a page somebody simply may not use into the
 * ochre P4 reserves for a decision they have to take.
 *
 * **It is decided from the role, not from a 403.** The page never asks: `abilitiesOf`
 * already knows the answer, and a request whose only purpose is to be refused is a
 * round trip that spends a reader's time to tell them something the session said. The
 * sentence is the one the API would have sent — `security/permissions.ts` `refusal()`
 * writes "That role cannot raise or comment on a request" for `requests:write` — in
 * the second person and about the page rather than the write, because what the reader
 * did was open a page.
 *
 * **The way back is Services & billing**, not the dashboard: the roles this refuses
 * are the ones `.docs/19` says are there for the money, and it is the page the same
 * sentence tells them they do have.
 */

import { Link } from '@tanstack/react-router'

import { Empty } from '../../components/portal/Disclosure.js'
import { More } from '../../components/portal/Text.js'

export type RoleRefusalProps = {
  /**
   * What the reader cannot see, as the plural noun the page is called by —
   * "requests", "projects", "files". It completes "Your role does not see …", so it
   * is a bare noun and carries no full stop of its own.
   */
  readonly what: string
}

export function RoleRefusal({ what }: RoleRefusalProps) {
  return (
    <Empty>
      Your role does not see {what}.{' '}
      <More asChild>
        <Link to="/app/billing">Services &amp; billing &rarr;</Link>
      </More>
    </Empty>
  )
}
